import { useState, useCallback } from "react";
import { Key, Plus, Trash2, Copy, Check, ShieldAlert, Clock, ShieldCheck } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import {
  useEnterpriseApiKeys,
  useCreateEnterpriseApiKey,
  useRevokeEnterpriseApiKey,
} from "@/hooks/admin/useEnterprise";
import type {
  EnterpriseApiKey,
  CreateApiKeyInput,
  CreateApiKeyResponse,
} from "@/lib/admin-api/enterprise";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AVAILABLE_SCOPES: readonly { readonly value: string; readonly label: string }[] = [
  { value: "read", label: "Lecture" },
  { value: "write", label: "Ecriture" },
  { value: "admin", label: "Administration" },
  { value: "orders", label: "Commandes" },
  { value: "products", label: "Produits" },
  { value: "delivery", label: "Livraison" },
];

const EXPIRATION_PRESETS: readonly { readonly value: string; readonly label: string }[] = [
  { value: "30", label: "30 jours" },
  { value: "90", label: "90 jours" },
  { value: "365", label: "1 an" },
  { value: "custom", label: "Personnalise" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(d: string | null): string {
  if (!d) return "\u2014";
  return new Date(d).toLocaleDateString("fr-SN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatDateTime(d: string | null): string {
  if (!d) return "\u2014";
  return new Date(d).toLocaleDateString("fr-SN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function addDaysFromNow(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().split("T")[0];
}

type KeyStatus = "active" | "expired" | "revoked";

function getKeyStatus(key: EnterpriseApiKey): KeyStatus {
  if (!key.is_active) return "revoked";
  if (key.expires_at && new Date(key.expires_at) < new Date()) return "expired";
  return "active";
}

const STATUS_CONFIG: Record<KeyStatus, { readonly label: string; readonly className: string }> = {
  active: {
    label: "Actif",
    className: "bg-emerald-100 text-emerald-700 border-emerald-200",
  },
  expired: {
    label: "Expire",
    className: "bg-amber-100 text-amber-700 border-amber-200",
  },
  revoked: {
    label: "Revoque",
    className: "bg-red-100 text-red-700 border-red-200",
  },
};

// ---------------------------------------------------------------------------
// Create API Key Modal
// ---------------------------------------------------------------------------

interface CreateKeyModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly isSaving: boolean;
  readonly onSave: (input: CreateApiKeyInput) => Promise<CreateApiKeyResponse>;
  readonly onKeyCreated: (response: CreateApiKeyResponse) => void;
}

function CreateKeyModal({
  open,
  onClose,
  isSaving,
  onSave,
  onKeyCreated,
}: CreateKeyModalProps) {
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<readonly string[]>([]);
  const [expirationPreset, setExpirationPreset] = useState("90");
  const [customDate, setCustomDate] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setName("");
    setScopes([]);
    setExpirationPreset("90");
    setCustomDate("");
    setError(null);
    onClose();
  }, [onClose]);

  const toggleScope = useCallback((scope: string) => {
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  }, []);

  const getExpiresAt = useCallback((): string | undefined => {
    if (expirationPreset === "custom") {
      return customDate || undefined;
    }
    const days = parseInt(expirationPreset, 10);
    if (isNaN(days)) return undefined;
    return addDaysFromNow(days);
  }, [expirationPreset, customDate]);

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) {
      setError("Le nom est requis.");
      return;
    }
    if (scopes.length === 0) {
      setError("Selectionnez au moins une portee.");
      return;
    }
    if (expirationPreset === "custom" && !customDate) {
      setError("Veuillez choisir une date d'expiration.");
      return;
    }
    setError(null);
    try {
      const response = await onSave({
        name: name.trim(),
        scopes,
        expires_at: getExpiresAt(),
      });
      onKeyCreated(response);
      toast.success("Cle API creee avec succes");
      handleClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Une erreur est survenue.";
      setError(message);
      toast.error("Echec de la creation de la cle API");
    }
  }, [name, scopes, expirationPreset, customDate, onSave, onKeyCreated, handleClose, getExpiresAt]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Creer une cle API"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Creation..." : "Creer la cle"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {error && (
          <div className="flex items-start gap-2 rounded-md bg-red-50 px-3 py-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Name */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Nom <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="Ex. : Integration ERP"
          />
        </div>

        {/* Scopes */}
        <div>
          <label className="mb-2 block text-sm font-medium text-stone-700">
            Portees <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {AVAILABLE_SCOPES.map((scope) => (
              <label
                key={scope.value}
                className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2.5 text-sm transition-colors ${
                  scopes.includes(scope.value)
                    ? "border-amber-300 bg-amber-50 text-amber-900"
                    : "border-stone-200 text-stone-700 hover:bg-stone-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={scopes.includes(scope.value)}
                  onChange={() => toggleScope(scope.value)}
                  className="rounded border-stone-300 text-amber-600 focus:ring-amber-500"
                />
                <span>{scope.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Expiration */}
        <div>
          <label className="mb-2 block text-sm font-medium text-stone-700">
            Expiration
          </label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {EXPIRATION_PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                onClick={() => setExpirationPreset(preset.value)}
                className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                  expirationPreset === preset.value
                    ? "border-amber-300 bg-amber-50 text-amber-900"
                    : "border-stone-200 text-stone-600 hover:bg-stone-50"
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          {expirationPreset === "custom" && (
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              className="mt-2 w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          )}
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Key Reveal Modal
// ---------------------------------------------------------------------------

interface KeyRevealModalProps {
  readonly response: CreateApiKeyResponse | null;
  readonly onClose: () => void;
}

function KeyRevealModal({ response, onClose }: KeyRevealModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (!response) return;
    navigator.clipboard.writeText(response.key);
    setCopied(true);
    toast.success("Cle copiee dans le presse-papiers");
    setTimeout(() => setCopied(false), 2000);
  }, [response]);

  if (!response) return null;

  return (
    <AdminModal
      open={Boolean(response)}
      onClose={onClose}
      title="Cle API creee"
      size="md"
      footer={
        <Button onClick={onClose}>Fermer</Button>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div>
              <p className="text-sm font-semibold text-amber-900">
                Copiez cette cle maintenant
              </p>
              <p className="mt-0.5 text-xs text-amber-700">
                Cette cle ne sera plus jamais affichee. Conservez-la en lieu sur.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <code className="flex-1 overflow-x-auto rounded-md border border-stone-200 bg-stone-900 px-4 py-3 font-mono text-sm text-emerald-400">
            {response.key}
          </code>
          <button
            onClick={handleCopy}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-stone-200 bg-white text-stone-600 transition-colors hover:bg-stone-50"
            title="Copier"
          >
            {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 rounded-lg bg-stone-50 p-3 text-sm">
          <div>
            <p className="text-xs text-stone-500">Nom</p>
            <p className="font-medium text-stone-900">{response.apiKey.name}</p>
          </div>
          <div>
            <p className="text-xs text-stone-500">Prefixe</p>
            <p className="font-mono font-medium text-stone-900">{response.apiKey.prefix}</p>
          </div>
          <div>
            <p className="text-xs text-stone-500">Portees</p>
            <div className="mt-0.5 flex flex-wrap gap-1">
              {response.apiKey.scopes.map((s) => (
                <span
                  key={s}
                  className="inline-flex rounded border border-stone-200 bg-white px-1.5 py-0.5 font-mono text-xs text-stone-600"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs text-stone-500">Expiration</p>
            <p className="font-medium text-stone-900">
              {response.apiKey.expires_at ? formatDate(response.apiKey.expires_at) : "Jamais"}
            </p>
          </div>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ApiKeys() {
  useAdminPageTitle("Cles API");

  const [showCreate, setShowCreate] = useState(false);
  const [newKeyResponse, setNewKeyResponse] = useState<CreateApiKeyResponse | null>(null);

  const { data, isLoading, isError } = useEnterpriseApiKeys();
  const createMutation = useCreateEnterpriseApiKey();
  const revokeMutation = useRevokeEnterpriseApiKey();

  const keys = data?.keys ?? [];

  const activeCount = keys.filter((k) => getKeyStatus(k) === "active").length;
  const expiredCount = keys.filter((k) => getKeyStatus(k) === "expired").length;
  const revokedCount = keys.filter((k) => getKeyStatus(k) === "revoked").length;
  const lastUsed = keys
    .filter((k) => k.last_used_at)
    .sort((a, b) =>
      new Date(b.last_used_at!).getTime() - new Date(a.last_used_at!).getTime(),
    )[0];

  const handleRevoke = useCallback(
    async (id: string, name: string) => {
      if (!confirm(`Revoquer la cle "${name}" ? Cette action est irreversible.`)) return;
      try {
        await revokeMutation.mutateAsync(id);
        toast.success(`Cle "${name}" revoquee`);
      } catch {
        toast.error("Echec de la revocation de la cle");
      }
    },
    [revokeMutation],
  );

  const handleCreate = useCallback(
    async (input: CreateApiKeyInput): Promise<CreateApiKeyResponse> => {
      return createMutation.mutateAsync(input);
    },
    [createMutation],
  );

  const columns: readonly ColumnDef<EnterpriseApiKey>[] = [
    {
      id: "name",
      header: "Nom",
      cell: (row) => (
        <div>
          <span className="font-medium text-stone-900">{row.name}</span>
          <p className="mt-0.5 text-xs text-stone-400">par {row.created_by}</p>
        </div>
      ),
    },
    {
      id: "prefix",
      header: "Prefixe",
      cell: (row) => (
        <code className="rounded bg-stone-100 px-2 py-0.5 font-mono text-xs text-stone-700">
          {row.prefix}...
        </code>
      ),
    },
    {
      id: "scopes",
      header: "Portees",
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.scopes.map((s) => (
            <span
              key={s}
              className="inline-flex items-center rounded border border-stone-200 bg-stone-50 px-1.5 py-0.5 font-mono text-xs text-stone-600"
            >
              {s}
            </span>
          ))}
        </div>
      ),
    },
    {
      id: "created_at",
      header: "Creee le",
      cell: (row) => (
        <span className="text-xs text-stone-500">{formatDate(row.created_at)}</span>
      ),
    },
    {
      id: "expires_at",
      header: "Expiration",
      cell: (row) => {
        if (!row.expires_at) return <span className="text-xs text-stone-400">Jamais</span>;
        const expired = new Date(row.expires_at) < new Date();
        const daysLeft = Math.ceil(
          (new Date(row.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
        );
        return (
          <div>
            <span className={`text-xs ${expired ? "font-medium text-red-600" : "text-stone-500"}`}>
              {formatDate(row.expires_at)}
            </span>
            {!expired && daysLeft <= 30 && (
              <p className="mt-0.5 flex items-center gap-1 text-xs text-amber-600">
                <Clock className="h-3 w-3" />
                {daysLeft}j restants
              </p>
            )}
          </div>
        );
      },
    },
    {
      id: "last_used_at",
      header: "Derniere utilisation",
      cell: (row) => (
        <span className="text-xs text-stone-500">{formatDateTime(row.last_used_at)}</span>
      ),
    },
    {
      id: "status",
      header: "Statut",
      cell: (row) => {
        const status = getKeyStatus(row);
        const config = STATUS_CONFIG[status];
        return (
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${config.className}`}
          >
            {status === "active" && <ShieldCheck className="h-3 w-3" />}
            {status === "expired" && <Clock className="h-3 w-3" />}
            {status === "revoked" && <ShieldAlert className="h-3 w-3" />}
            {config.label}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row) =>
        row.is_active && getKeyStatus(row) !== "expired" ? (
          <button
            onClick={() => handleRevoke(row.id, row.name)}
            className="flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-100"
            title="Revoquer"
          >
            <Trash2 className="h-3 w-3" />
            Revoquer
          </button>
        ) : null,
    },
  ];

  if (isError) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <ShieldAlert className="h-10 w-10 text-red-400" />
          <p className="mt-3 text-sm font-medium text-stone-700">
            Erreur lors du chargement des cles API
          </p>
          <p className="mt-1 text-xs text-stone-500">
            Veuillez reessayer ou contacter le support.
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <Key className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">Cles API</h1>
              <p className="text-sm text-stone-500">
                Gestion des cles d'acces API de niveau entreprise
              </p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Creer une cle API
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard
            label="Cles actives"
            value={activeCount}
            icon={<ShieldCheck className="h-4 w-4" />}
          />
          <KPICard
            label="Cles expirees"
            value={expiredCount}
            icon={<Clock className="h-4 w-4" />}
          />
          <KPICard
            label="Cles revoquees"
            value={revokedCount}
            icon={<ShieldAlert className="h-4 w-4" />}
          />
          <KPICard
            label="Derniere utilisation"
            value={lastUsed ? formatDateTime(lastUsed.last_used_at) : "\u2014"}
          />
        </div>

        {/* Table */}
        <AdminDataTable
          columns={columns}
          data={keys}
          isLoading={isLoading}
          emptyMessage="Aucune clé API trouvée."
        />
      </div>

      <CreateKeyModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        isSaving={createMutation.isPending}
        onSave={handleCreate}
        onKeyCreated={setNewKeyResponse}
      />

      <KeyRevealModal
        response={newKeyResponse}
        onClose={() => setNewKeyResponse(null)}
      />
    </AdminLayout>
  );
}
